import React from 'react';
import { Paper, Typography, Box, Button, useTheme } from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';
import LockIcon from '@mui/icons-material/Lock';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import type { BoxProps, TypographyProps } from '@mui/material';
import { keyframes } from '@mui/system';

const MotionPaper = motion(Paper);
const MotionBox = motion<BoxProps>(Box);
const MotionTypography = motion<TypographyProps>(Typography);

const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-15px); }
  100% { transform: translateY(0px); }
`;

const floatArrowAnimation = keyframes`
  0% { transform: translateY(0px); opacity: 0.5; }
  50% { transform: translateY(10px); opacity: 1; }
  100% { transform: translateY(0px); opacity: 0.5; }
`;

const pulseAnimation = keyframes`
  0% { transform: scale(1); opacity: 0.3; }
  50% { transform: scale(1.1); opacity: 0.1; }
  100% { transform: scale(1); opacity: 0.3; }
`;

const shimmerAnimation = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;



const floatSquareAnimation = keyframes`
  0% { 
    transform: rotate(45deg) translate(0, 0) scale(1);
    border-color: rgba(63, 160, 255, 0.2);
    box-shadow: 0 0 20px rgba(63, 160, 255, 0.1);
  }
  50% { 
    transform: rotate(45deg) translate(0, -15px) scale(1.1);
    border-color: rgba(63, 160, 255, 0.4);
    box-shadow: 0 0 30px rgba(63, 160, 255, 0.2);
  }
  100% { 
    transform: rotate(45deg) translate(0, 0) scale(1);
    border-color: rgba(63, 160, 255, 0.2);
    box-shadow: 0 0 20px rgba(63, 160, 255, 0.1);
  }
`;

const WelcomeSection = () => {
  const theme = useTheme();
  // No scroll effects

  return (
    <MotionPaper
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      elevation={0}
      sx={{
        pt: { xs: 2, sm: 3, md: 4 },
        pb: { xs: 4, sm: 6, md: 8 },
        mt: { xs: 2, sm: 3, md: 4 },
        mb: { xs: 4, sm: 6, md: 8 },
        backgroundColor: 'rgba(63, 160, 255, 0.08)',
        borderRadius: 4,
        backdropFilter: 'blur(20px)',
        border: '2px solid rgba(63, 160, 255, 0.15)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        position: 'relative',
        overflow: 'hidden',
        zIndex: 1,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(45deg, transparent 48%, rgba(63, 160, 255, 0.08) 50%, transparent 52%),
            linear-gradient(-45deg, transparent 48%, rgba(63, 160, 255, 0.08) 50%, transparent 52%)
          `,
          backgroundSize: '60px 60px',
          opacity: 4,
          zIndex: 2
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '150%',
          height: '150%',
          background: 'radial-gradient(circle at center, rgba(63, 160, 255, 0.06) 0%, transparent 70%)',
          opacity: 0.7,
          zIndex: 1
        }
      }}
    >
      <Box 
        sx={{
          maxWidth: '800px',
          width: '100%',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.25,
          pb: { xs: 3, sm: 4, md: 5 }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 0.25,
            position: 'relative',
          }}
        >
          <Box
            component="img"
            src="/next-basket-robot-pointing-compressed-2.png"
            alt="Next Basket Robot"
            sx={{
              width: { xs: 200, sm: 240, md: 280 },
              height: { xs: 200, sm: 240, md: 280 },
              objectFit: 'contain',
              filter: 'drop-shadow(0 4px 20px rgba(63, 160, 255, 0.2))',
              animation: `${floatAnimation} 3s ease-in-out infinite`,
              transition: theme.custom.transition,
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
          />
        </Box>
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Typography
            variant="h1"
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
              fontWeight: 900,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              mb: 0.25,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #2c2c2c 0%, #1a1a1a 20%, #4a4a4a 40%, #2c2c2c 60%, #1a1a1a 80%, #4a4a4a 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 50%, rgba(255,255,255,0.1) 100%)',
                pointerEvents: 'none'
              }
            }}
          >
            WELCOME TO THE <Box component="span" sx={{ background: `linear-gradient(135deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 70%, #1a4b80 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', display: 'inline' }}>NEXT BASKET</Box> PRESENTATION PORTAL
          </Typography>
        </Box>

        <Box
          sx={{
            width: '100%',
            height: '40px',
            position: 'relative',
            mb: 4,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '200px',
              height: '2px',
              background: 'linear-gradient(90deg, transparent, rgba(63, 160, 255, 0.5), transparent)'
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '10px',
              height: '10px',
              background: '#fff',
              border: '2px solid rgba(63, 160, 255, 0.3)',
              borderRadius: '50%',
              boxShadow: '0 0 10px rgba(63, 160, 255, 0.2)'
            }
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 40,
            left: 40,
            width: '60px',
            height: '60px',
            border: '1px solid rgba(63, 160, 255, 0.2)',
            borderRadius: '4px',
            zIndex: 4,
            background: 'linear-gradient(135deg, rgba(63, 160, 255, 0.05) 0%, rgba(63, 160, 255, 0.02) 100%)',
            animation: `${floatSquareAnimation} 4s ease-in-out infinite`,
            transition: 'all 0.3s ease-in-out',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: -2,
              border: '1px solid rgba(63, 160, 255, 0.15)',
              borderRadius: '4px',
              background: 'linear-gradient(135deg, rgba(63, 160, 255, 0.1) 0%, transparent 100%)'
            }
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 40,
            right: 40,
            width: '60px',
            height: '60px',
            border: '1px solid rgba(63, 160, 255, 0.2)',
            borderRadius: '4px',
            zIndex: 4,
            background: 'linear-gradient(135deg, rgba(63, 160, 255, 0.05) 0%, rgba(63, 160, 255, 0.02) 100%)',
            animation: `${floatSquareAnimation} 4s ease-in-out infinite 1s`,
            transition: 'all 0.3s ease-in-out',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: -2,
              border: '1px solid rgba(63, 160, 255, 0.15)',
              borderRadius: '4px',
              background: 'linear-gradient(135deg, rgba(63, 160, 255, 0.1) 0%, transparent 100%)'
            }
          }}
        />
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
            lineHeight: 1.8,
            textAlign: 'center',
            maxWidth: '700px',
            mx: 'auto',
          }}
        >
          We are pleased to provide you with exclusive access to our Presentation and supporting documents,
          containing detailed insights into NEXT BASKET's development
          roadmap
        </Typography>
        <Box
          sx={{
            position: 'absolute',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            zIndex: 5
          }}
        >
          <Button
            sx={{
              minWidth: { xs: '40px', sm: '50px', md: '50px' },
              width: { xs: '40px', sm: '50px', md: '50px' },
              height: { xs: '40px', sm: '50px', md: '50px' },
              p: 0,
              borderRadius: '50%',
              backgroundColor: 'rgba(63, 160, 255, 0.1)',
              border: '1px solid rgba(63, 160, 255, 0.3)',
              backdropFilter: 'blur(8px)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(63, 160, 255, 0.2), rgba(63, 160, 255, 0.1))',
                opacity: 0,
                transition: 'all 0.3s ease'
              },
              '&:hover': {
                backgroundColor: 'rgba(63, 160, 255, 0.15)',
                borderColor: 'rgba(63, 160, 255, 0.5)',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 20px rgba(63, 160, 255, 0.2)',
                '&::before': {
                  opacity: 1
                },
                '& .MuiSvgIcon-root': {
                  transform: 'translateY(2px)',
                  color: 'rgb(63, 160, 255)'
                }
              }
            }}
          >
            <KeyboardArrowDownIcon
              sx={{
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                color: 'rgba(63, 160, 255, 0.9)',
                animation: `${floatArrowAnimation} 2s ease-in-out infinite`,
                filter: 'drop-shadow(0 0 10px rgba(63, 160, 255, 0.4))',
                transition: 'all 0.3s ease-in-out',
                zIndex: 1,
                mixBlendMode: 'screen',
                '&:hover': {
                  color: '#fff',
                  filter: 'drop-shadow(0 0 20px rgba(63, 160, 255, 0.8))'
                }
              }}
            />
          </Button>
        </Box>
      </Box>
    </MotionPaper>
  );
};

export default WelcomeSection;
